import { ApiResult, FieldType, isNullOrUndefined, ViewModelBase } from "@shoothill/core";
import { action, computed, observable } from "mobx";
import { AppUrls } from "AppUrls";
import { RejectedCPSListModel } from "./RejectedCPSListModel";
import { RejectedCPSListItemsAndRelatedDTO } from "./RejectedCPSListItemsAndRelatedDTO";
import { RejectedCPSListItemViewModel } from "./RejectedCPSListItemViewModel";
import { RejectedCPSListFilterViewModel } from "./RejectedCPSListFilterViewModel";
//import { RejectedCPSListFilterParamsBaseModelDTO } from "./RejectedCPSListFilterParamsBaseModel";
import debounce from "lodash-es/debounce";
import { RejectedCPSListFilterParamsModelDTO } from "./RejectedCPSListFilterParamsModel";
import { VariationStatusTypeModel } from "Globals/Models/Domain/VariationStatusTypeModel";

export class RejectedCPSListViewModel extends ViewModelBase<RejectedCPSListModel> {
    private static _instance: RejectedCPSListViewModel;
    public static get Instance() {
        return this._instance || (this._instance = new this());
    }
    constructor() {
        super(new RejectedCPSListModel(), false);
        this.setDecorators(RejectedCPSListModel);
    }

    @computed
    public get getSearchString() {
        return RejectedCPSListFilterViewModel.Instance.model.searchText;
    }

    public getSearchStringComputed = () => {
        return computed(() => RejectedCPSListFilterViewModel.Instance.model.searchText);
    };

    public handleSearchChange = (val: string, ieid: string) => {
        RejectedCPSListFilterViewModel.Instance.setValue("searchText", val);

        if (val.length > 3 || val.length === 0) {
            RejectedCPSListFilterViewModel.Instance.setValue("pageNumber", 1);
            this.reloadDataDelayed(ieid);
        }
    };

    private reloadDataDelayed = debounce((ieid: string) => {
        this.apiGetAll(ieid);
    }, 500);

    @action
    public cleanUp = () => {
        this.reloadDataDelayed.cancel();
        this.setHasLoaded(false);
    };

    @action
    public resetDateFilters = () => {
        this.model.filterEndDateINV = null;
        this.model.filterStartDateINV = null;
    };

    @observable
    public errorMessage: string = "";

    @observable
    public rejectedCPSViewModels: RejectedCPSListItemViewModel[] = [];

    @action
    private populateListItemViewModels = (dto: RejectedCPSListItemsAndRelatedDTO) => {
        const rejectedCPSViewModels2: RejectedCPSListItemViewModel[] = [];
        for (const rejectedCPSListItem of dto.variations) {
            let itemModel = new RejectedCPSListModel();
            itemModel.fromDto(rejectedCPSListItem);
            const statusTypeModel = new VariationStatusTypeModel(dto.variationStatusType.find((pst) => pst.id === rejectedCPSListItem.variationStatusId));
            rejectedCPSViewModels2.push(new RejectedCPSListItemViewModel(itemModel, statusTypeModel));
        }
        this.rejectedCPSViewModels = rejectedCPSViewModels2;
    };

    public apiGetAll = async (ieid: string): Promise<ApiResult<RejectedCPSListItemsAndRelatedDTO>> => {
        return this.apiGetDateAllRejectedCPSs(ieid);
    };

    @action
    public clear = (ieid: string) => {
        RejectedCPSListFilterViewModel.Instance.model.reset();
        this.apiGetAll(ieid);
    };

    // Api GetAll Record

    @action
    public setHasLoaded = (val: boolean) => {
        this.hasLoaded = val;
    };

    // Used to ensure that only the results of the last request are used to populate the table.
    private lastRequestId = 0;

    @observable
    public hasLoaded: boolean = false;

    // End Api GetAll Record

    @action
    public setFromDateFilter = (val: Date | null) => {
        this.model.filterStartDateINV = val;
    };

    @action
    public setToDateFilter = (val: Date | null) => {
        this.model.filterEndDateINV = val;
    };

    // RejectedCPS by Date

    public apiGetDateAllRejectedCPSs = async (val: string): Promise<ApiResult<RejectedCPSListItemsAndRelatedDTO>> => {
        this.setIsLoading(true);
        const currentRequestId = ++this.lastRequestId;
        let paramsViewModel = RejectedCPSListFilterViewModel.Instance;
        let params: RejectedCPSListFilterParamsModelDTO = paramsViewModel.model.toDto();
        params.initialLoad = !this.hasLoaded;

        const request: RejectedCPSListAndRelatedRequest = {
            id: val,
            startDatefilter: this.model.filterStartDateINV,
            endDateFilter: this.model.filterEndDateINV,
            filterParams: params,
        };
        let apiResult = await this.Post<RejectedCPSListItemsAndRelatedDTO>(AppUrls.Server.Variation.GetRejectedCPSList, request);
        if (apiResult) {
            if (currentRequestId === this.lastRequestId && apiResult.wasSuccessful) {
                this.populateListItemViewModels(apiResult.payload);
                RejectedCPSListFilterViewModel.Instance.setTotalCount(apiResult.payload.totalCount);

                if (!this.hasLoaded) {
                    RejectedCPSListFilterViewModel.Instance.setStatuses(apiResult.payload.variationStatusType, true);
                }
            } else {
                console.log(apiResult.errors);
            }
        }
        this.setIsLoading(false);
        this.setHasLoaded(true);
        return apiResult;
    };

    // end RejectedCPS By Date

    public async isFieldValid(fieldName: keyof FieldType<RejectedCPSListModel>): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}

export interface RequestModel {
    Id: string;
}

export class RejectedCPSListAndRelatedRequest {
    id: string = "";
    startDatefilter?: Date | null = null;
    endDateFilter?: Date | null = null;
    filterParams: RejectedCPSListFilterParamsModelDTO | null = null;
}
